/**
 * Resolve the bookmarked Community Cipher for a Space (if any).
 */

import { useMemo, useCallback, useEffect, useSyncExternalStore } from 'react';
import type { CommunityCipher } from '@adieuu/crypto';
import { useCipherStore } from '../../hooks/useCipherStore';
import {
  bumpCipherLinkEpoch,
  getCipherLinkEpoch,
  getSpaceCipherLink,
  getSpaceKey,
  getSpaceKeyEpoch,
  subscribeCipherLinks,
  subscribeSpaceKeys,
} from '../../services/spaceCipherService';

/**
 * Returns the unlocked Cipher linked to `spaceId`, or null when missing /
 * locked. Re-resolves automatically when cipher links change (hydrate,
 * bookmark, detect). Warms the Argon2 per-Space key so message/metadata
 * decrypt can peek synchronously. Call `bumpCipherLink` only if you need a
 * manual refresh.
 */
export function useSpaceCipher(spaceId: string | null | undefined): {
  spaceCipher: CommunityCipher | null;
  bumpCipherLink: () => void;
} {
  const { getCipherKey } = useCipherStore();
  const linkEpoch = useSyncExternalStore(
    subscribeCipherLinks,
    getCipherLinkEpoch,
    getCipherLinkEpoch,
  );
  useSyncExternalStore(subscribeSpaceKeys, getSpaceKeyEpoch, getSpaceKeyEpoch);

  const bumpCipherLink = useCallback(() => {
    bumpCipherLinkEpoch();
  }, []);

  const spaceCipher = useMemo(() => {
    if (!spaceId) return null;
    const localCipherId = getSpaceCipherLink(spaceId);
    if (!localCipherId) return null;
    return getCipherKey(localCipherId);
  }, [spaceId, getCipherKey, linkEpoch]);

  useEffect(() => {
    if (!spaceCipher || !spaceId) return;
    void getSpaceKey(spaceCipher, spaceId);
  }, [spaceCipher, spaceId]);

  return { spaceCipher, bumpCipherLink };
}
